import React, { useState, useEffect, useCallback } from 'react';
import { doc, setDoc, deleteDoc, onSnapshot, serverTimestamp, Timestamp } from 'firebase/firestore';
import { QRCodeSVG } from 'qrcode.react';
import { db, auth } from '../firebase/firebase';
import { Tablet, X, RefreshCw, Trash2, Copy, Check } from 'lucide-react';

const EXPIRY_MINUTES = 15;

function generateToken() {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  return Array.from({ length: 6 }, () => chars[Math.floor(Math.random() * chars.length)]).join('');
}

function formatDate(val) {
  if (!val) return '—';
  const d = val?.toDate ? val.toDate() : new Date(val);
  if (Number.isNaN(d.getTime())) return '—';
  return new Intl.DateTimeFormat('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  }).format(d);
}

export default function IpadSetupModal({ open, onClose, project }) {
  const [loading, setLoading] = useState(true);
  const [pairedDevice, setPairedDevice] = useState(null);
  const [pairingToken, setPairingToken] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [revoking, setRevoking] = useState(false);
  const [copied, setCopied] = useState(false);
  const [confirmRevoke, setConfirmRevoke] = useState(false);

  const projectId = project?.id;
  const projectCode = project?.projectCode;

  // Real-time listener on pairedDevice — auto-updates when iPad completes pairing
  useEffect(() => {
    if (!open || !projectId) return undefined;
    setLoading(true);
    setPairingToken(null);
    setConfirmRevoke(false);

    const deviceRef = doc(db, `projects/${projectId}/ipadConfig/pairedDevice`);
    const unsub = onSnapshot(
      deviceRef,
      (snap) => {
        setPairedDevice(snap.exists() ? snap.data() : null);
        setLoading(false);
      },
      () => {
        setPairedDevice(null);
        setLoading(false);
      },
    );
    return unsub;
  }, [open, projectId]);

  // When the iPad pairs while QR is shown, dismiss the QR — device is now connected
  useEffect(() => {
    if (pairedDevice && pairingToken) {
      setPairingToken(null);
    }
  }, [pairedDevice, pairingToken]);

  const handleGenerate = useCallback(async () => {
    setGenerating(true);
    try {
      const token = generateToken();
      const expiresAt = Timestamp.fromDate(
        new Date(Date.now() + EXPIRY_MINUTES * 60 * 1000),
      );
      await setDoc(doc(db, `projects/${projectId}/ipadConfig/pairingToken`), {
        token,
        expiresAt,
        createdBy: auth.currentUser?.uid,
        createdAt: serverTimestamp(),
        used: false,
      });
      setPairingToken({ token, expiresAt: expiresAt.toDate() });
    } catch (err) {
      console.error('Error generating pairing token:', err);
      alert('Failed to generate pairing code. Please try again.');
    } finally {
      setGenerating(false);
    }
  }, [projectId]);

  // Auto-refresh the pairing code when it nears expiry (every EXPIRY_MINUTES)
  useEffect(() => {
    if (!pairingToken) return undefined;
    const ms = EXPIRY_MINUTES * 60 * 1000;
    const timer = setTimeout(() => { handleGenerate(); }, ms);
    return () => clearTimeout(timer);
  }, [pairingToken, handleGenerate]);

  const handleRevoke = async () => {
    setRevoking(true);
    try {
      await deleteDoc(doc(db, `projects/${projectId}/ipadConfig/pairedDevice`));
      setConfirmRevoke(false);
    } catch (err) {
      console.error('Error revoking device:', err);
      alert('Failed to revoke device. Please try again.');
    } finally {
      setRevoking(false);
    }
  };

  const handleCopy = () => {
    if (!pairingToken) return;
    const text = projectCode
      ? `Project code: ${projectCode}  Pairing code: ${pairingToken.token}`
      : `Pairing code: ${pairingToken.token}`;
    navigator.clipboard.writeText(text).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleClose = () => {
    if (!revoking && !generating) {
      setPairingToken(null);
      setConfirmRevoke(false);
      onClose();
    }
  };

  if (!open) return null;

  const qrValue = pairingToken
    ? `crewchain://pair?p=${encodeURIComponent(projectId)}&t=${pairingToken.token}`
    : '';

  return (
    <div
      style={{
        position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh',
        background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center',
        justifyContent: 'center', zIndex: 2000,
      }}
      onClick={handleClose}
    >
      <div
        style={{
          background: 'var(--cc-card-bg)', borderRadius: 16, padding: 28,
          width: '90%', maxWidth: 460, maxHeight: '90vh', overflowY: 'auto',
          boxShadow: '0 8px 32px rgba(0,0,0,0.18)', boxSizing: 'border-box',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Tablet size={20} color="var(--branding-app-primary)" />
            <h3 style={{ fontSize: 17, fontWeight: 600, color: 'var(--cc-text-primary)', margin: 0 }}>Tablet Setup</h3>
          </div>
          <button
            type="button"
            onClick={handleClose}
            style={{
              background: 'none', border: 'none', cursor: 'pointer', color: 'var(--cc-text-muted)',
              display: 'flex', alignItems: 'center', padding: 4, borderRadius: 4,
            }}
          >
            <X size={20} />
          </button>
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: '32px 0', color: 'var(--branding-app-surface-panel-hint)', fontSize: 14 }}>
            Loading…
          </div>
        ) : pairedDevice && !pairingToken ? (
          /* ── DEVICE PAIRED ── */
          <div>
            <div style={{
              background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 10,
              padding: '16px 18px', marginBottom: 20,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#22c55e', flexShrink: 0 }} />
                <span style={{ fontWeight: 600, fontSize: 14, color: '#166534' }}>Device paired</span>
              </div>
              <div style={{ fontSize: 12, color: 'var(--cc-text-primary)', display: 'flex', flexDirection: 'column', gap: 4 }}>
                <span>Paired: {formatDate(pairedDevice.pairedAt)}</span>
                {pairedDevice.lastActive && (
                  <span>Last active: {formatDate(pairedDevice.lastActive)}</span>
                )}
              </div>
            </div>

            <p style={{ fontSize: 12, color: 'var(--cc-text-muted)', marginBottom: 16, lineHeight: 1.5 }}>
              Revoking removes the device's access immediately. You can pair a replacement afterwards.
            </p>

            {confirmRevoke ? (
              <div style={{ display: 'flex', gap: 10 }}>
                <button
                  type="button"
                  onClick={() => setConfirmRevoke(false)}
                  style={{
                    flex: 1, background: 'var(--cc-cancel-bg)', color: 'var(--cc-text-primary)',
                    border: 'none', borderRadius: 8, padding: '10px 0',
                    fontWeight: 500, cursor: 'pointer', fontSize: 14,
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleRevoke}
                  disabled={revoking}
                  style={{
                    flex: 1, background: revoking ? '#fca5a5' : '#ef4444',
                    color: '#fff', border: 'none', borderRadius: 8, padding: '10px 0',
                    fontWeight: 600, cursor: revoking ? 'not-allowed' : 'pointer', fontSize: 14,
                  }}
                >
                  {revoking ? 'Revoking…' : 'Confirm revoke'}
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setConfirmRevoke(true)}
                style={{
                  width: '100%', background: 'transparent', color: '#ef4444',
                  border: '1px solid #fca5a5', borderRadius: 8, padding: '10px 0',
                  fontWeight: 500, cursor: 'pointer', fontSize: 14,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                }}
              >
                <Trash2 size={14} />
                Revoke device pairing
              </button>
            )}
          </div>
        ) : pairingToken ? (
          /* ── PAIRING IN PROGRESS — show QR ── */
          <div>
            <div style={{ textAlign: 'center', marginBottom: 20 }}>
              <div style={{
                display: 'inline-block', padding: 16,
                background: 'var(--branding-app-surface-panel)', borderRadius: 12, border: '1px solid var(--branding-app-surface-panel-border)',
                marginBottom: 14,
              }}>
                <QRCodeSVG value={qrValue} size={184} level="M" />
              </div>
              <p style={{ fontSize: 12, color: 'var(--cc-text-muted)', margin: 0 }}>
                Scan this code on CrewChain on the tablet to connect it to <strong style={{ color: 'var(--cc-text-primary)' }}>{project?.name}</strong>.
              </p>
            </div>

            {/* Manual fallback */}
            <div style={{
              background: 'var(--branding-app-surface-panel, #f8fafc)',
              borderRadius: 10, padding: '14px 16px', marginBottom: 14,
              border: '1px solid var(--branding-app-surface-panel-border)',
            }}>
              <div style={{
                fontSize: 11, color: 'var(--cc-text-muted)', fontWeight: 600, marginBottom: 10,
                textTransform: 'uppercase', letterSpacing: '0.5px',
              }}>
                Or enter manually on device
              </div>
              {projectCode && (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <span style={{ fontSize: 12, color: 'var(--cc-text-secondary)' }}>Project code</span>
                  <span style={{
                    fontSize: 24, fontWeight: 800, color: 'var(--cc-text-primary)',
                    letterSpacing: 6, fontVariantNumeric: 'tabular-nums',
                  }}>
                    {projectCode}
                  </span>
                </div>
              )}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: 12, color: 'var(--cc-text-secondary)' }}>Pairing code</span>
                <span style={{
                  fontSize: 24, fontWeight: 800, color: 'var(--branding-app-primary)',
                  letterSpacing: 6, fontVariantNumeric: 'tabular-nums',
                }}>
                  {pairingToken.token}
                </span>
              </div>
            </div>

            <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
              <button
                type="button"
                onClick={handleCopy}
                style={{
                  flex: 1,
                  background: copied ? '#f0fdf4' : '#e5e7eb',
                  color: copied ? '#166534' : 'var(--cc-text-secondary)',
                  border: copied ? '1px solid #bbf7d0' : '1px solid #d1d5db',
                  borderRadius: 8, padding: '8px 0', fontWeight: 500,
                  cursor: 'pointer', fontSize: 13,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5,
                  transition: 'all 0.2s',
                }}
              >
                {copied ? <Check size={13} /> : <Copy size={13} />}
                {copied ? 'Copied' : 'Copy codes'}
              </button>
              <button
                type="button"
                onClick={handleGenerate}
                disabled={generating}
                style={{
                  flex: 1, background: 'var(--cc-cancel-bg)', color: 'var(--cc-text-primary)',
                  border: '1px solid #d1d5db', borderRadius: 8, padding: '8px 0',
                  fontWeight: 500, cursor: generating ? 'not-allowed' : 'pointer',
                  fontSize: 13,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5,
                }}
              >
                <RefreshCw size={13} />
                New code
              </button>
            </div>

            <p style={{ fontSize: 11, color: 'var(--branding-app-surface-panel-hint)', textAlign: 'center', margin: 0 }}>
              Codes refresh automatically every {EXPIRY_MINUTES} minutes. This modal updates when the device connects.
            </p>
          </div>
        ) : (
          /* ── NO DEVICE PAIRED ── */
          <div>
            <div style={{
              background: 'var(--branding-app-surface-panel, #f8fafc)',
              borderRadius: 10, padding: '28px 20px', textAlign: 'center',
              marginBottom: 20, border: '1px solid var(--branding-app-surface-panel-border)',
            }}>
              <Tablet size={38} color="#cbd5e1" />
              <p style={{ fontSize: 14, color: 'var(--branding-app-surface-panel-hint)', margin: '10px 0 0 0' }}>
                No device paired to this project
              </p>
            </div>

            <button
              type="button"
              onClick={handleGenerate}
              disabled={generating}
              className="cc-btn-primary"
              style={{
                width: '100%', padding: '12px 0', borderRadius: 8,
                fontWeight: 600, fontSize: 14, border: 'none',
                cursor: generating ? 'not-allowed' : 'pointer',
                opacity: generating ? 0.7 : 1,
              }}
            >
              {generating ? 'Generating…' : 'Pair Device'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
